import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { notification } from "antd";
import { FoCard, FoDialog, formatEgp, statusBadge } from "./financeUi";
import { os } from "./osClient";
import { PagerTable, SelectField, TextField, VendorSelect, useOsResource, useVendorOptions, vendorName } from "./osForm";
import { invoiceActions, pageMeta } from "./osRules";

const blank = { vendorId: "", period: "", amount: "", dueDate: "", notes: "", description: "" };

export default function InvoiceRegister() {
  const vendors = useVendorOptions();
  const [params] = useSearchParams();
  const [vendorId, setVendorId] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState(params.get("search") || "");
  const [applied, setApplied] = useState({ vendorId: "", status: "", search: params.get("search") || "" });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(blank);
  const [busy, setBusy] = useState(false);
  const list = useOsResource(
    (auth) => os.invoices(auth, { ...applied, page, limit }),
    [applied, page, limit]
  );
  const pack = pageMeta(list.data);
  const actions = invoiceActions(editing?.status);

  const save = async () => {
    if (!draft.period || !(Number(draft.amount) >= 0)) {
      notification.error({ message: "Period and amount are required." });
      return;
    }
    const lineItems = draft.description ? [{ description: draft.description, qty: 1, unitPrice: Number(draft.amount) || 0, total: Number(draft.amount) || 0 }] : undefined;
    const body = {
      period: draft.period,
      amount: Number(draft.amount) || 0,
      dueDate: draft.dueDate || undefined,
      notes: draft.notes,
      lineItems,
    };
    setBusy(true);
    try {
      if (editing) {
        if (actions.editVendor && draft.vendorId) body.vendorId = draft.vendorId;
        await os.updateInvoice(list.token, editing._id, body);
        notification.success({ message: "Invoice updated." });
      } else {
        if (!draft.vendorId) {
          notification.error({ message: "Choose a brand." });
          setBusy(false);
          return;
        }
        await os.createInvoice(list.token, { ...body, vendorId: draft.vendorId });
        notification.success({ message: "Draft invoice created." });
      }
      setOpen(false);
      setEditing(null);
      list.reload();
    } catch (err) {
      /* toast already shown */
    } finally {
      setBusy(false);
    }
  };

  const act = async (invoice, action) => {
    setBusy(true);
    try {
      if (action === "pdf") await os.downloadInvoice(list.token, invoice._id, invoice.invoiceNumber);
      else {
        await os.invoiceAction(list.token, invoice._id, action);
        notification.success({ message: "Invoice updated." });
        list.reload();
      }
    } catch (err) {
      /* toast already shown */
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fo-stack">
      <div className="fo-tools">
        <VendorSelect vendors={vendors} value={vendorId} onChange={setVendorId} emptyLabel="All brands" />
        <SelectField label="Status" value={status} onChange={setStatus} allowEmpty options={["Draft", "Sent", "Due", "Paid", "Overdue", "Escalated"]} />
        <TextField label="Search" value={search} onChange={setSearch} />
        <button type="button" className="fo-btn" onClick={() => { setPage(1); setApplied({ vendorId, status, search: search.trim() }); }}>Apply</button>
        <button type="button" className="fo-btn fo-btn-primary" onClick={() => { setEditing(null); setDraft(blank); setOpen(true); }}>New invoice</button>
      </div>
      <FoCard title="Invoice register" note="Sent invoices move to Due on the due date and Overdue after it. This is separate from the statement PDF extract.">
        <PagerTable
          loading={list.loading}
          dataSource={pack.docs}
          rowKey="_id"
          page={page}
          limit={limit}
          total={pack.total}
          minWidth={980}
          onChange={(nextPage, nextLimit) => { setPage(nextPage); setLimit(nextLimit); }}
          empty="No invoices match."
          columns={[
            { title: "Number", dataIndex: "invoiceNumber", render: (value) => <span className="fo-mono">{value}</span> },
            { title: "Brand", dataIndex: "vendor", render: (vendor) => vendorName(vendor) },
            { title: "Period", dataIndex: "period" },
            { title: "Amount", dataIndex: "amount", render: (value) => `${formatEgp(value)} EGP` },
            { title: "Due", dataIndex: "dueDate" },
            { title: "Status", dataIndex: "status", render: (value) => statusBadge(value) },
            {
              title: "",
              key: "actions",
              render: (_, invoice) => {
                const allowed = invoiceActions(invoice.status);
                return (
                  <div className="fo-tier-acts">
                    {allowed.edit && <button type="button" className="fo-btn" onClick={() => {
                      setEditing(invoice);
                      setDraft({
                        vendorId: invoice.vendor?._id || invoice.vendorId || "",
                        period: invoice.period || "",
                        amount: String(invoice.amount ?? ""),
                        dueDate: invoice.dueDate ? String(invoice.dueDate).slice(0, 10) : "",
                        notes: invoice.notes || "",
                        description: invoice.lineItems?.[0]?.description || "",
                      });
                      setOpen(true);
                    }}>Edit</button>}
                    {allowed.send && <button type="button" className="fo-btn" disabled={busy} onClick={() => act(invoice, "send")}>Send</button>}
                    {allowed.remind && <button type="button" className="fo-btn" disabled={busy} onClick={() => act(invoice, "remind")}>Remind</button>}
                    {allowed.paid && <button type="button" className="fo-btn" disabled={busy} onClick={() => act(invoice, "paid")}>Mark paid</button>}
                    {allowed.escalate && <button type="button" className="fo-btn" disabled={busy} onClick={() => act(invoice, "escalate")}>Escalate</button>}
                    <button type="button" className="fo-btn" disabled={busy} onClick={() => act(invoice, "pdf")}>PDF</button>
                  </div>
                );
              },
            },
          ]}
        />
      </FoCard>
      <FoDialog open={open} title={editing ? editing.invoiceNumber : "New invoice"} onClose={() => !busy && setOpen(false)} wide>
        <div className="fo-form-grid">
          {(!editing || actions.editVendor) && (
            <VendorSelect vendors={vendors} value={draft.vendorId} onChange={(value) => setDraft({ ...draft, vendorId: value })} emptyLabel="Select a brand" />
          )}
          <TextField label="Period" value={draft.period} onChange={(period) => setDraft({ ...draft, period })} hint="YYYY-MM" />
          <TextField label="Amount" type="number" value={draft.amount} onChange={(amount) => setDraft({ ...draft, amount })} />
          <TextField label="Due date" type="date" value={draft.dueDate} onChange={(dueDate) => setDraft({ ...draft, dueDate })} hint="Defaults to 14 days out." />
          <TextField label="Line description" value={draft.description} onChange={(description) => setDraft({ ...draft, description })} />
          <TextField label="Notes" value={draft.notes} onChange={(notes) => setDraft({ ...draft, notes })} />
        </div>
        <div className="fo-tier-acts" style={{ marginTop: 18 }}>
          <button type="button" className="fo-btn fo-btn-primary" disabled={busy} onClick={save}>Save</button>
        </div>
      </FoDialog>
    </div>
  );
}
